import { Divider, Typography } from 'antd';

import {
    DescriptionReportComponent,
    EmbedReportComponent,
    MultiStatistic,
    ReportTable,
    SingleStatistic,
} from '@/shared/components/data-visualization/ant-design';
import {
    createGlobalTradeDatasetQuery,
    createGlobalTradeViewDefinition,
    DEFAULT_TRADE_FILTERS,
    DEFAULT_TRADE_MAP_VIEW,
    GLOBAL_TRADE_DATASET_ID,
    GlobalTradeGlobeReportComponent,
} from '@/shared/components/data-visualization/global-trade';
import MapReportComponent from '@/shared/components/data-visualization/maplibre/00-Map';
import { Vantage } from '@/shared/components/data-visualization/monaco-editor';
import {
    AreaChart,
    BarChart,
    ChordChart,
    DonutChart,
    FunnelChart,
    GaugeChart,
    HeatMap,
    LineChart,
    PieChart,
    RadarChart,
    RadialChart,
    SankeyChart,
    ScatterPlot,
} from '@/shared/components/data-visualization/nivo';
import { userFacingFeatures } from '@/shared/constants';
import { ReportComponentType } from '@/types';
import {
    DataSourceType,
    DescriptionConfig,
    GlobeConfig,
    MapConfig,
    ReportComponentConfig,
    ReportComponentDefinition,
    StatisticConfig,
    TableConfig,
    TitleConfig,
    VantageConfig,
} from '@/types/api/modules/business-intelligence';

import { EMBED_REPORT_COMPONENT_TYPES } from './reportComponents/embedValidation';

export const useReportComponentDefinitions = () => {
    const CARTESIAN_AXES = {
        xAxis: { metric: true, groupBy: true, sortBy: true },
        yAxis: { metric: true, groupBy: false, sortBy: true },
    };

    const REPORT_COMPONENT_DEFINITIONS: Record<string, ReportComponentDefinition> = {
        [ReportComponentType.TITLE]: {
            type: ReportComponentType.TITLE,
            label: 'Title',
            category: 'content',
            previewAspectRatio: '3 / 1',
            capabilities: {
                overview: { dataSource: false, dateRange: false, filters: false },
                formatting: false,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: false,
                },
            },
            requirements: { required: [] },
            renderer: ({ component }) => {
                const config = component.configuration as Partial<TitleConfig> | undefined;

                return (
                    <div style={{ padding: '1rem' }}>
                        <Typography.Title level={2} style={{ margin: 0 }}>
                            {config?.text ?? component.name ?? 'Untitled Section'}
                        </Typography.Title>
                    </div>
                );
            },
            defaultConfig: (): TitleConfig => ({
                version: 1,
                type: ReportComponentType.TITLE,
                text: 'Untitled Section',
                style: {
                    align: 'left',
                    size: 'lg',
                },
            }),
        },
        [ReportComponentType.DESCRIPTION]: {
            type: ReportComponentType.DESCRIPTION,
            label: 'Description',
            category: 'content',
            previewAspectRatio: '2 / 1',
            capabilities: {
                overview: { dataSource: false, dateRange: false, filters: false },
                formatting: false,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: false,
                },
            },
            requirements: { required: [] },
            renderer: ({ component }) => <DescriptionReportComponent component={component} persistenceMode='draft' />,
            defaultConfig: (): DescriptionConfig => ({
                version: 1,
                type: ReportComponentType.DESCRIPTION,
                body: '',
            }),
        },
        [ReportComponentType.DIVIDER]: {
            type: ReportComponentType.DIVIDER,
            label: 'Divider',
            category: 'content',
            previewAspectRatio: '3 / 1',
            capabilities: {
                overview: { dataSource: false, dateRange: false, filters: false },
                formatting: false,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: false,
                },
            },
            requirements: { required: [] },
            renderer: () => <Divider style={{ margin: '2rem 1rem' }} />,
            defaultConfig: () =>
                ({
                    version: 1,
                    type: ReportComponentType.DIVIDER,
                }) as unknown as ReportComponentConfig,
        },
        [ReportComponentType.STATISTIC]: {
            type: ReportComponentType.STATISTIC,
            label: 'Key Statistic',
            category: 'chart',
            previewAspectRatio: '2 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: false, sortBy: false },
                yAxis: { metric: false, groupBy: false, sortBy: false },
                additionalSettings: {
                    zeroValues: true,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: { required: [] },
            renderer: ({ component }) => <SingleStatistic component={component} />,
            defaultConfig: (): StatisticConfig => ({
                version: 1,
                type: ReportComponentType.STATISTIC,
                label: 'Key Statistic',
                value: 0,
                formatting: {
                    attribution: true,
                },
            }),
        },
        [ReportComponentType.MULTIPLE_STATISTICS]: {
            type: ReportComponentType.MULTIPLE_STATISTICS,
            label: 'Statistic Group',
            category: 'chart',
            previewAspectRatio: '2 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: false, sortBy: false },
                additionalSettings: {
                    zeroValues: true,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: { required: [] },
            renderer: ({ component }) => <MultiStatistic component={component} />,
            defaultConfig: () =>
                ({
                    version: 1,
                    type: ReportComponentType.MULTIPLE_STATISTICS,
                    formatting: { attribution: true },
                }) as unknown as ReportComponentConfig,
        },
        [ReportComponentType.MAP]: {
            type: ReportComponentType.MAP,
            label: 'Map',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: false, groupBy: true, sortBy: false },
                yAxis: { metric: true, groupBy: true, sortBy: false },
                additionalSettings: {
                    zeroValues: true,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: [],
            },
            renderer: ({ component }) => {
                return <MapReportComponent component={component} />;
            },
            defaultConfig: (): MapConfig => ({
                version: 1,
                type: ReportComponentType.MAP,
            }),
        },
        [ReportComponentType.GLOBE]: {
            type: ReportComponentType.GLOBE,
            label: 'Globe',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: false, dateRange: false, filters: false },
                formatting: false,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: [],
            },
            renderer: ({ component }) => <GlobalTradeGlobeReportComponent component={component} />,
            defaultConfig: (): GlobeConfig => {
                const view = createGlobalTradeViewDefinition({
                    filters: DEFAULT_TRADE_FILTERS,
                    mapView: DEFAULT_TRADE_MAP_VIEW,
                    projection: 'globe',
                    showFlows: true,
                    showHubs: true,
                });

                return {
                    dataSet: {
                        key: GLOBAL_TRADE_DATASET_ID,
                        type: DataSourceType.GLOBAL_TRADE,
                    },
                    query: createGlobalTradeDatasetQuery(view),
                    version: 1,
                    type: ReportComponentType.GLOBE,
                    view,
                };
            },
        },
        [ReportComponentType.TABLE]: {
            type: ReportComponentType.TABLE,
            label: 'Table',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                ...CARTESIAN_AXES,
                additionalSettings: {
                    zeroValues: true,
                    averageLine: true,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['list'],
            },
            renderer: ({ component }) => <ReportTable component={component} />,
            defaultConfig: (): TableConfig => ({
                version: 1,
                type: ReportComponentType.TABLE,

                overview: {
                    filters: [],
                },

                columns: [],

                pagination: {
                    pageSize: 25,
                },

                formatting: {
                    attribution: true,
                },
            }),
        },
        // Charts
        [ReportComponentType.BAR_CHART]: {
            type: ReportComponentType.BAR_CHART,
            label: 'Bar Chart',
            category: 'chart',
            previewAspectRatio: '1 / 1',

            capabilities: {
                overview: {
                    dataSource: true,
                    dateRange: true,
                    filters: true,
                },
                formatting: true,
                xAxis: { metric: true, groupBy: true, sortBy: true },
                yAxis: { metric: true, groupBy: false, sortBy: true },
                additionalSettings: {
                    zeroValues: true,
                    averageLine: true,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },

            requirements: {
                required: ['dataSource', 'xAxisMetric', 'yAxisMetric'],
            },
            renderer: ({ component }) => {
                return <BarChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.BAR_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.LINE_CHART]: {
            type: ReportComponentType.LINE_CHART,
            label: 'Line Chart',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                ...CARTESIAN_AXES,
                additionalSettings: {
                    zeroValues: true,
                    averageLine: true,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'xAxisMetric', 'yAxisMetric'],
            },
            renderer: ({ component }) => {
                return <LineChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.LINE_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.AREA_CHART]: {
            type: ReportComponentType.AREA_CHART,
            label: 'Area Chart',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                ...CARTESIAN_AXES,
                additionalSettings: {
                    zeroValues: true,
                    averageLine: true,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <AreaChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.AREA_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.SCATTER_PLOT]: {
            type: ReportComponentType.SCATTER_PLOT,
            label: 'Scatter Plot',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: false, sortBy: false },
                yAxis: { metric: true, groupBy: false, sortBy: false },
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'xAxisMetric', 'yAxisMetric'],
            },
            renderer: ({ component }) => {
                return <ScatterPlot component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.SCATTER_PLOT,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.PIE_CHART]: {
            type: ReportComponentType.PIE_CHART,
            label: 'Pie Chart',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: true, sortBy: false },
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <PieChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.PIE_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.DONUT_CHART]: {
            type: ReportComponentType.DONUT_CHART,
            label: 'Donut Chart',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: true, sortBy: false },
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <DonutChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.DONUT_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.GAUGE_CHART]: {
            type: ReportComponentType.GAUGE_CHART,
            label: 'Gauge',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: false },
                formatting: true,
                xAxis: undefined,
                yAxis: { metric: true, groupBy: false, sortBy: false },
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric'],
            },
            renderer: ({ component }) => {
                return <GaugeChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.GAUGE_CHART,
                formatting: {
                    attribution: true,
                },
            }),
        },

        [ReportComponentType.HEAT_MAP]: {
            type: ReportComponentType.HEAT_MAP,
            label: 'Heat Map',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: false, groupBy: true, sortBy: false },
                yAxis: { metric: false, groupBy: true, sortBy: false },
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: true,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <HeatMap component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.HEAT_MAP,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.FUNNEL_CHART]: {
            type: ReportComponentType.FUNNEL_CHART,
            label: 'Funnel',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: true, sortBy: false },
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: true,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <FunnelChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.FUNNEL_CHART,
                formatting: {
                    attribution: true,
                },
            }),
        },

        [ReportComponentType.SANKEY_CHART]: {
            type: ReportComponentType.SANKEY_CHART,
            label: 'Sankey',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: undefined,
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <SankeyChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.SANKEY_CHART,
                formatting: {
                    attribution: true,
                },
            }),
        },

        [ReportComponentType.RADAR_CHART]: {
            type: ReportComponentType.RADAR_CHART,
            label: 'Radar',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: true, sortBy: false },
                yAxis: { metric: true, groupBy: false, sortBy: false },
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: true,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <RadarChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.RADAR_CHART,
                formatting: {
                    attribution: true,
                    showLegend: true,
                },
            }),
        },

        [ReportComponentType.RADIAL_CHART]: {
            type: ReportComponentType.RADIAL_CHART,
            label: 'Radial',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: { metric: true, groupBy: false, sortBy: false },
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric'],
            },
            renderer: ({ component }) => {
                return <RadialChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.RADIAL_CHART,
                formatting: {
                    attribution: true,
                },
            }),
        },

        [ReportComponentType.CHORD_CHART]: {
            type: ReportComponentType.CHORD_CHART,
            label: 'Chord',
            category: 'chart',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: true, dateRange: true, filters: true },
                formatting: true,
                xAxis: undefined,
                yAxis: undefined,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: true,
                },
            },
            requirements: {
                required: ['dataSource', 'metric', 'groupBy'],
            },
            renderer: ({ component }) => {
                return <ChordChart component={component} numerator={1} denominator={1} />;
            },
            defaultConfig: () => ({
                version: 1,
                type: ReportComponentType.CHORD_CHART,
                formatting: {
                    attribution: true,
                },
            }),
        },
    };

    if (!userFacingFeatures.spatialReportComponents) {
        delete REPORT_COMPONENT_DEFINITIONS[ReportComponentType.MAP];
        delete REPORT_COMPONENT_DEFINITIONS[ReportComponentType.GLOBE];
    }

    REPORT_COMPONENT_DEFINITIONS[ReportComponentType.COUNTERCYCLICAL_VANTAGE] = {
        type: ReportComponentType.COUNTERCYCLICAL_VANTAGE,
        label: 'Vantage',
        category: 'table',
        previewAspectRatio: '1 / 1',
        capabilities: {
            overview: { dataSource: false, dateRange: false, filters: false },
            formatting: false,
            additionalSettings: {
                zeroValues: false,
                averageLine: false,
                axisLabels: false,
                legend: false,
                attribution: false,
            },
        },
        requirements: { required: [] },
        renderer: ({ component }) => <Vantage component={component} executeWhenReadOnly readOnly />,
        defaultConfig: (): VantageConfig => ({
            version: 1,
            type: ReportComponentType.COUNTERCYCLICAL_VANTAGE,
            displayMode: 'codeAndVisualization',
            limit: 500,
            visualization: 'table',
        }),
    };

    EMBED_REPORT_COMPONENT_TYPES.forEach((type) => {
        REPORT_COMPONENT_DEFINITIONS[type] = {
            type,
            label: type,
            category: 'content',
            previewAspectRatio: '1 / 1',
            capabilities: {
                overview: { dataSource: false, dateRange: false, filters: false },
                formatting: false,
                additionalSettings: {
                    zeroValues: false,
                    averageLine: false,
                    axisLabels: false,
                    legend: false,
                    attribution: false,
                },
            },
            requirements: { required: [] },
            renderer: ({ component }) => <EmbedReportComponent component={component} />,
            defaultConfig: () =>
                ({
                    version: 1,
                    type,
                    url: '',
                }) as unknown as ReportComponentConfig,
        };
    });

    return { REPORT_COMPONENT_DEFINITIONS, CARTESIAN_AXES };
};
